/**
 * StaffManagementTab - Staff accounts, discounts, and tab tracking for payroll
 * Features:
 * - List all staff with their discount % and tab balance
 * - Edit staff discount %
 * - View/add staff tab entries
 * - Clear tab (mark as paid)
 * - Generate payroll report
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Users, Percent, DollarSign, Plus, CreditCard, FileText,
  Eye, Edit2, Check, X, Loader2, RefreshCw, Receipt,
  Clock, AlertCircle, Crown, User, Wallet, Download,
  ChevronDown, ChevronUp, Trash2, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

export default function StaffManagementTab({ authHeaders }) {
  const [staffList, setStaffList] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [tabHistory, setTabHistory] = useState([]);
  const [showAddTab, setShowAddTab] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showPayrollReport, setShowPayrollReport] = useState(false);
  const [payrollReport, setPayrollReport] = useState(null);
  const [payrollDates, setPayrollDates] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0], // Last 7 days
    end: new Date().toISOString().split('T')[0]
  });
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [payrollWeeks, setPayrollWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  
  // Edit states
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [newDiscount, setNewDiscount] = useState('');
  
  // Add tab form
  const [tabForm, setTabForm] = useState({
    amount: '',
    description: '',
    items: ''
  });
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    method: 'payroll_deduction',
    notes: ''
  });
  
  const [actionLoading, setActionLoading] = useState(null);

  const fetchStaffTabs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/staff-tabs/summary');
      setStaffList(res.data.staff || []);
      setTotalOutstanding(res.data.total_outstanding || 0);
    } catch (err) {
      console.error('Failed to fetch staff tabs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPayrollWeeks = useCallback(async () => {
    try {
      const res = await api.get('/admin/staff-tabs/payroll-weeks');
      setPayrollWeeks(res.data.weeks || []);
      // Auto-select current week
      if (res.data.weeks?.length > 0) {
        const currentWeek = res.data.weeks[0];
        setSelectedWeek(currentWeek);
        setPayrollDates({
          start: currentWeek.start_date.split('T')[0],
          end: currentWeek.end_date.split('T')[0]
        });
      }
    } catch (err) {
      console.error('Failed to fetch payroll weeks:', err);
    }
  }, []);

  useEffect(() => {
    fetchStaffTabs();
    fetchPayrollWeeks();
  }, [fetchStaffTabs, fetchPayrollWeeks]);

  const fetchTabHistory = async (staffId) => {
    try {
      const res = await api.get(`/admin/staff-tabs/${staffId}?limit=50`);
      setTabHistory(res.data.entries || []);
    } catch (err) {
      toast.error('Failed to fetch tab history');
    }
  };

  const handleSelectStaff = async (staff) => {
    setSelectedStaff(staff);
    setShowAddTab(false);
    setShowPayment(false);
    await fetchTabHistory(staff.id);
  };

  const handleUpdateDiscount = async (staffId) => {
    const discount = parseFloat(newDiscount);
    if (isNaN(discount) || discount < 0 || discount > 100) {
      toast.error('Discount must be between 0 and 100');
      return;
    }
    
    setActionLoading('discount');
    try {
      const res = await api.put(`/admin/staff-members/${staffId}/discount?discount_percent=${discount}`);
      toast.success(res.data.message);
      setEditingDiscount(null);
      fetchStaffTabs();
    } catch (err) {
      toast.error('Failed to update discount');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddTabEntry = async () => {
    const amount = parseFloat(tabForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!tabForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    setActionLoading('add-tab');
    try {
      const res = await api.post('/admin/staff-tabs/add', {
        staff_id: selectedStaff.id,
        amount,
        description: tabForm.description,
        items: tabForm.items ? tabForm.items.split(',').map(i => ({ name: i.trim() })) : null
      });
      
      toast.success(res.data.message);
      setShowAddTab(false);
      setTabForm({ amount: '', description: '', items: '' });
      fetchStaffTabs();
      fetchTabHistory(selectedStaff.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add tab entry');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRecordPayment = async () => {
    const amount = parseFloat(paymentForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setActionLoading('payment');
    try {
      const res = await api.post('/admin/staff-tabs/payment', {
        staff_id: selectedStaff.id,
        amount,
        payment_method: paymentForm.method,
        notes: paymentForm.notes || null
      });
      
      toast.success(res.data.message);
      setShowPayment(false);
      setPaymentForm({ amount: '', method: 'payroll_deduction', notes: '' });
      fetchStaffTabs();
      fetchTabHistory(selectedStaff.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearTab = async () => {
    if (!selectedStaff?.tab_balance || selectedStaff.tab_balance <= 0) {
      toast.error('No balance to clear');
      return;
    }
    
    if (!confirm(`Clear ${selectedStaff.name}'s entire tab balance of $${selectedStaff.tab_balance.toFixed(2)}?`)) {
      return;
    }
    
    setActionLoading('clear');
    try {
      const res = await api.post(`/admin/staff-tabs/${selectedStaff.id}/clear`);
      toast.success(res.data.message);
      fetchStaffTabs();
      fetchTabHistory(selectedStaff.id);
      setSelectedStaff(prev => ({ ...prev, tab_balance: 0 }));
    } catch (err) {
      toast.error('Failed to clear tab');
    } finally {
      setActionLoading(null);
    }
  };

  const fetchPayrollReport = async () => {
    setActionLoading('report');
    try {
      const params = new URLSearchParams();
      if (payrollDates.start) params.append('start_date', new Date(payrollDates.start).toISOString());
      if (payrollDates.end) params.append('end_date', new Date(payrollDates.end + 'T23:59:59').toISOString());
      
      const res = await api.get(`/admin/staff-tabs/payroll-report?${params}`);
      setPayrollReport(res.data);
      setShowPayrollReport(true);
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setActionLoading(null);
    }
  };

  // Process payroll - clears all staff tabs using the new batch endpoint
  const processPayroll = async () => {
    if (!window.confirm('Process payroll and clear all staff tabs? This will mark all outstanding balances as paid via payroll deduction.')) {
      return;
    }
    
    setProcessingPayroll(true);
    try {
      const weekEnding = selectedWeek?.week_ending || new Date().toISOString().split('T')[0];
      const res = await api.post(`/admin/staff-tabs/process-payroll?week_ending=${weekEnding}&notes=Payroll+processed+via+Admin`);
      
      toast.success(res.data.message || `Payroll processed! ${res.data.cleared_count} staff tabs cleared.`);
      setShowPayrollReport(false);
      fetchStaffTabs();
      setPayrollReport(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process payroll');
    } finally {
      setProcessingPayroll(false);
    }
  };

  // Handle week selection
  const handleWeekSelect = (week) => {
    setSelectedWeek(week);
    setPayrollDates({
      start: week.start_date.split('T')[0],
      end: week.end_date.split('T')[0]
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      case 'mgr-sup': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400';
      case 'server': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'cashier': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      case 'display': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400';
      // Legacy roles for backwards compatibility
      case 'manager': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      case 'supervisor': return 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400';
      case 'staff': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return Crown;
      case 'mgr-sup': return Shield;
      case 'server': return User;
      case 'cashier': return CreditCard;
      case 'display': return Eye;
      case 'manager': return Crown;
      case 'supervisor': return Shield;
      default: return User;
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'owner': 'Owner',
      'mgr-sup': 'Mgr-Sup',
      'server': 'Server',
      'cashier': 'Cashier',
      'display': 'Display',
      'manager': 'Manager (Legacy)',
      'supervisor': 'Supervisor (Legacy)',
      'staff': 'Staff (Legacy)'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-management-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <Users className="w-6 h-6 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Staff Management</h2>
            <p className="text-muted-foreground text-sm">Manage discounts and track staff tabs</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={fetchPayrollReport}
            disabled={actionLoading === 'report'}
            data-testid="payroll-report-btn"
          >
            {actionLoading === 'report' ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Payroll Report
          </Button>
          <Button variant="outline" size="sm" onClick={fetchStaffTabs}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{staffList.length}</div>
              <div className="text-sm text-muted-foreground">Staff Members</div>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">${totalOutstanding.toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">Total Tab Balance</div>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">
                {staffList.filter(s => s.tab_balance > 0).length}
              </div>
              <div className="text-sm text-muted-foreground">With Open Tabs</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Staff List */}
        <div className="col-span-1 bg-card rounded-2xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-heading text-lg text-foreground">Staff List</h3>
          </div>
          <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
            {staffList.map(staff => (
              <button
                key={staff.id}
                onClick={() => handleSelectStaff(staff)}
                className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                  selectedStaff?.id === staff.id ? 'bg-primary/5 border-l-4 border-primary' : ''
                }`}
                data-testid={`staff-item-${staff.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-foreground">{staff.name}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${getRoleColor(staff.role)}`}>
                    {(() => {
                      const IconComponent = getRoleIcon(staff.role);
                      return <IconComponent className="w-3 h-3" />;
                    })()}
                    {getRoleLabel(staff.role)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Percent className="w-3 h-3" />
                    {staff.discount_percent || 0}% discount
                  </span>
                  {staff.tab_balance > 0 ? (
                    <span className="text-red-600 font-medium">${staff.tab_balance.toFixed(2)}</span>
                  ) : (
                    <span className="text-green-600 text-xs">No balance</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Staff Details */}
        <div className="col-span-2">
          {selectedStaff ? (
            <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
              {/* Staff Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                    {(() => {
                      const IconComponent = getRoleIcon(selectedStaff.role);
                      return <IconComponent className="w-8 h-8 text-violet-600" />;
                    })()}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{selectedStaff.name}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${getRoleColor(selectedStaff.role)}`}>
                      {(() => {
                        const IconComponent = getRoleIcon(selectedStaff.role);
                        return <IconComponent className="w-3 h-3" />;
                      })()}
                      {getRoleLabel(selectedStaff.role)}
                    </span>
                  </div>
                </div>
                
                {/* Tab Balance */}
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">Tab Balance</div>
                  <div className={`text-3xl font-bold ${
                    selectedStaff.tab_balance > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    ${(selectedStaff.tab_balance || 0).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Discount Setting */}
              <div className="p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Staff Discount</div>
                    {editingDiscount === selectedStaff.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={newDiscount}
                          onChange={(e) => setNewDiscount(e.target.value)}
                          className="w-24 h-9"
                          placeholder="0-100"
                          data-testid="discount-input"
                        />
                        <span className="text-muted-foreground">%</span>
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateDiscount(selectedStaff.id)}
                          disabled={actionLoading === 'discount'}
                          data-testid="save-discount-btn"
                        >
                          {actionLoading === 'discount' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingDiscount(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-foreground">
                          {selectedStaff.discount_percent || 0}%
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setEditingDiscount(selectedStaff.id);
                            setNewDiscount(String(selectedStaff.discount_percent || 0));
                          }}
                          data-testid="edit-discount-btn"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <Percent className="w-10 h-10 text-muted-foreground/30" />
                </div>
              </div>

              {/* Tab Actions */}
              <div className="flex gap-3">
                <Button 
                  onClick={() => { setShowAddTab(true); setShowPayment(false); }}
                  className="flex-1"
                  data-testid="add-tab-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Tab
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => { setShowPayment(true); setShowAddTab(false); }}
                  className="flex-1"
                  disabled={!selectedStaff.tab_balance}
                  data-testid="record-payment-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Record Payment
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleClearTab}
                  disabled={actionLoading === 'clear' || !selectedStaff.tab_balance}
                  className="text-red-600 hover:text-red-700 hover:border-red-300"
                  data-testid="clear-tab-btn"
                >
                  {actionLoading === 'clear' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {/* Add Tab Form */}
              {showAddTab && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl space-y-3">
                  <h4 className="font-medium text-foreground">Add Tab Entry</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-muted-foreground">Amount</label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={tabForm.amount}
                        onChange={(e) => setTabForm(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="tab-amount-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Description</label>
                      <Input
                        value={tabForm.description}
                        onChange={(e) => setTabForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="e.g., Lunch"
                        data-testid="tab-description-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Items (optional, comma-separated)</label>
                    <Input
                      value={tabForm.items}
                      onChange={(e) => setTabForm(prev => ({ ...prev, items: e.target.value }))}
                      placeholder="e.g., Coffee, Sandwich"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleAddTabEntry}
                      disabled={actionLoading === 'add-tab'}
                      data-testid="confirm-add-tab-btn"
                    >
                      {actionLoading === 'add-tab' ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Add Entry
                    </Button>
                    <Button variant="ghost" onClick={() => setShowAddTab(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Payment Form */}
              {showPayment && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl space-y-3">
                  <h4 className="font-medium text-foreground">Record Payment</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-muted-foreground">Amount</label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max={selectedStaff.tab_balance}
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Method</label>
                      <select
                        value={paymentForm.method}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, method: e.target.value }))}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                        data-testid="payment-method-select"
                      >
                        <option value="payroll_deduction">Payroll Deduction</option>
                        <option value="cash">Cash</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Notes (optional)</label>
                    <Input
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="e.g., Pay period ending Dec 15"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleRecordPayment}
                      disabled={actionLoading === 'payment'}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="confirm-payment-btn"
                    >
                      {actionLoading === 'payment' ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Record Payment
                    </Button>
                    <Button variant="ghost" onClick={() => setShowPayment(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Tab History */}
              <div>
                <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  Tab History
                </h4>
                {tabHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No tab history
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {tabHistory.map(entry => (
                      <div 
                        key={entry.id}
                        className={`p-3 rounded-lg ${
                          entry.type === 'charge' 
                            ? 'bg-red-50 dark:bg-red-900/10' 
                            : 'bg-green-50 dark:bg-green-900/10'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-foreground text-sm">{entry.description || entry.notes}</div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(entry.created_at).toLocaleDateString()} at {new Date(entry.created_at).toLocaleTimeString()}
                              {entry.order_number && <span className="ml-2">• Order #{entry.order_number}</span>}
                            </div>
                            
                            {/* Show items if available */}
                            {entry.items && entry.items.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-border/50">
                                <div className="text-xs font-medium text-muted-foreground mb-1">Items:</div>
                                <div className="space-y-1">
                                  {entry.items.map((item, idx) => (
                                    <div key={idx} className="flex justify-between text-xs text-foreground/80">
                                      <span>
                                        {item.quantity && item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}
                                      </span>
                                      {item.price != null && (
                                        <span className="text-muted-foreground">${(item.price * (item.quantity || 1)).toFixed(2)}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {entry.original_amount && entry.discount_applied && (
                                  <div className="mt-1 pt-1 border-t border-border/30 text-xs">
                                    <div className="flex justify-between text-muted-foreground">
                                      <span>Original:</span>
                                      <span>${entry.original_amount.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-green-600 dark:text-green-400">
                                      <span>Discount (50%):</span>
                                      <span>-${entry.discount_applied.toFixed(2)}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <span className={`font-bold ml-3 ${
                            entry.type === 'charge' ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {entry.type === 'charge' ? '+' : '-'}${Math.abs(entry.amount).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-card rounded-2xl border border-border p-12 text-center">
              <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Select a Staff Member</h3>
              <p className="text-muted-foreground">Choose a staff member from the list to view and manage their account</p>
            </div>
          )}
        </div>
      </div>

      {/* Payroll Report Modal */}
      {showPayrollReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPayrollReport(false)}>
          <div 
            className="bg-card rounded-2xl border border-border max-w-3xl w-full max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-foreground">Payroll Report</h3>
                <p className="text-sm text-muted-foreground">Staff meal deductions for payroll processing</p>
              </div>
              <button onClick={() => setShowPayrollReport(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Week Selector & Date Range */}
            <div className="p-4 bg-muted/30 border-b border-border">
              <div className="flex flex-col gap-3">
                {/* Week Quick Select */}
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-sm font-medium text-foreground whitespace-nowrap">Select Week:</label>
                  {payrollWeeks.map((week, idx) => (
                    <Button
                      key={idx}
                      variant={selectedWeek?.week_ending === week.week_ending ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleWeekSelect(week)}
                      className="text-xs"
                    >
                      {week.is_current ? 'This Week' : `Ending ${new Date(week.week_ending + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                    </Button>
                  ))}
                </div>
                
                {/* Selected Week Info */}
                {selectedWeek && (
                  <div className="bg-primary/10 px-3 py-2 rounded-lg text-sm">
                    <span className="font-medium text-primary">Week Ending: {new Date(selectedWeek.week_ending + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    <span className="text-muted-foreground ml-2">
                      (Mon {new Date(payrollDates.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - Sun {new Date(payrollDates.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                    </span>
                  </div>
                )}

                {/* Manual Date Override */}
                <div className="flex items-center gap-4 flex-wrap pt-2 border-t border-border/50">
                  <span className="text-xs text-muted-foreground">Or custom dates:</span>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground whitespace-nowrap">From:</label>
                    <Input
                      type="date"
                      value={payrollDates.start}
                      onChange={(e) => {
                        setPayrollDates(prev => ({ ...prev, start: e.target.value }));
                        setSelectedWeek(null); // Clear week selection when manually editing
                      }}
                      className="w-36 h-8 text-sm"
                      data-testid="payroll-start-date"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground whitespace-nowrap">To:</label>
                    <Input
                      type="date"
                      value={payrollDates.end}
                      onChange={(e) => {
                        setPayrollDates(prev => ({ ...prev, end: e.target.value }));
                        setSelectedWeek(null);
                      }}
                      className="w-36 h-8 text-sm"
                      data-testid="payroll-end-date"
                    />
                  </div>
                  <Button 
                    onClick={fetchPayrollReport}
                    disabled={actionLoading === 'report'}
                    size="sm"
                    data-testid="refresh-payroll-btn"
                  >
                    {actionLoading === 'report' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    <span className="ml-2">Load Report</span>
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-4 max-h-96 overflow-y-auto">
              {payrollReport ? (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
                      <div className="text-sm text-muted-foreground">Total Charges</div>
                      <div className="text-2xl font-bold text-red-600">${payrollReport.summary.total_charges.toFixed(2)}</div>
                    </div>
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                      <div className="text-sm text-muted-foreground">Total Payments</div>
                      <div className="text-2xl font-bold text-green-600">${payrollReport.summary.total_payments.toFixed(2)}</div>
                    </div>
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                      <div className="text-sm text-muted-foreground">Net Outstanding</div>
                      <div className="text-2xl font-bold text-amber-600">${payrollReport.summary.net_outstanding.toFixed(2)}</div>
                    </div>
                  </div>

                  {/* Staff Breakdown */}
                  <h4 className="font-medium text-foreground mb-3">Staff Breakdown</h4>
                  <div className="space-y-3">
                    {payrollReport.staff_reports.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No staff tab entries in this period
                      </div>
                    ) : (
                      payrollReport.staff_reports.map(staff => (
                        <div key={staff.staff_id} className="p-4 bg-muted/30 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-foreground">{staff.staff_name}</span>
                            <span className={`text-lg font-bold ${staff.net_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              ${staff.net_amount.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex gap-4 text-sm text-muted-foreground mb-2">
                            <span>Charges: ${staff.total_charges.toFixed(2)}</span>
                            <span>Payments: ${staff.total_payments.toFixed(2)}</span>
                            <span>{staff.entry_count} entries</span>
                          </div>
                          
                          {/* Individual Order Details */}
                          {staff.entries && staff.entries.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
                              <div className="text-xs font-medium text-muted-foreground">Order Details:</div>
                              {staff.entries.filter(e => e.type === 'charge' || e.entry_type === 'charge').map((entry, idx) => (
                                <div key={idx} className="bg-background/50 rounded-lg p-2 text-sm">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(entry.created_at).toLocaleDateString()} 
                                        {entry.order_number && ` • Order #${entry.order_number}`}
                                      </span>
                                    </div>
                                    <span className="font-medium text-red-600">+${entry.amount?.toFixed(2)}</span>
                                  </div>
                                  {entry.items && entry.items.length > 0 && (
                                    <div className="mt-1 pl-2 border-l-2 border-border/50">
                                      {entry.items.map((item, itemIdx) => (
                                        <div key={itemIdx} className="flex justify-between text-xs text-foreground/70">
                                          <span>{item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}</span>
                                          {item.price != null && <span>${(item.price * (item.quantity || 1)).toFixed(2)}</span>}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                  <p className="text-muted-foreground mt-2">Loading report...</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-border flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {payrollReport && `Generated: ${new Date(payrollReport.generated_at).toLocaleString()}`}
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={() => setShowPayrollReport(false)}>
                  Close
                </Button>
                
                {/* Download Excel for this week */}
                {payrollReport && selectedWeek && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/staff-tabs/weekly-export?week_start=${payrollDates.start}T00:00:00Z&week_end=${payrollDates.end}T23:59:59Z`;
                      window.open(url, '_blank');
                      toast.success('Downloading weekly report...');
                    }}
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    data-testid="download-weekly-excel-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Excel
                  </Button>
                )}
                
                {/* Clear This Week Only */}
                {payrollReport?.summary?.net_outstanding > 0 && selectedWeek && (
                  <Button 
                    onClick={async () => {
                      if (!window.confirm(`Clear staff charges ONLY for the week ${new Date(payrollDates.start).toLocaleDateString()} - ${new Date(payrollDates.end).toLocaleDateString()}?\n\nThis will:\n• Deduct these charges from payroll\n• Keep any NEW charges from this week untouched\n• Download receipt before clearing`)) {
                        return;
                      }
                      
                      // First download the Excel
                      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/staff-tabs/weekly-export?week_start=${payrollDates.start}T00:00:00Z&week_end=${payrollDates.end}T23:59:59Z`;
                      window.open(downloadUrl, '_blank');
                      
                      // Then clear
                      setProcessingPayroll(true);
                      try {
                        const res = await api.post(`/admin/staff-tabs/clear-week?week_start=${payrollDates.start}T00:00:00Z&week_end=${payrollDates.end}T23:59:59Z`);
                        toast.success(res.data.message || 'Week cleared successfully!');
                        setShowPayrollReport(false);
                        fetchStaffTabs();
                        setPayrollReport(null);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to clear week');
                      } finally {
                        setProcessingPayroll(false);
                      }
                    }}
                    disabled={processingPayroll}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="clear-week-btn"
                  >
                    {processingPayroll ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download & Clear This Week
                  </Button>
                )}
                
                {/* Old Process All button - keep for backwards compatibility but less prominent */}
                {payrollReport?.summary?.net_outstanding > 0 && !selectedWeek && (
                  <Button 
                    onClick={processPayroll}
                    disabled={processingPayroll}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="process-payroll-btn"
                  >
                    {processingPayroll ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Process Payroll & Clear All
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
